<template>
  <div class="h-full flex flex-col p-6">
    <!-- Back button (mobile) -->
    <IconButton
      v-if="isMobileDevice"
      class="absolute top-4 right-4 z-30"
      variant="text"
      size="sm"
      aria-label="Go back"
      @click="handleBack"
    >
      <ChevronLeftIcon class="w-5 h-5 text-text-tertiary" />
    </IconButton>

    <!-- Back button (desktop) -->
    <div
      v-if="!isMobileDevice"
      class="absolute flex items-center gap-2 px-4 pt-4 z-30"
    >
      <IconButton
        variant="text"
        size="sm"
        aria-label="Go back"
        @click="handleBack"
      >
        <ChevronLeftIcon class="w-5 h-5 text-text-tertiary" />
      </IconButton>
    </div>

    <!-- Layer 1: Category selection -->
    <div class="relative flex-1 min-h-0 overflow-hidden">
      <Transition :name="layerTransitionName">
        <div
          v-if="!selectedCategory"
          key="layer-category"
          class="absolute inset-0 flex flex-col items-center justify-center"
        >
          <div class="text-center mb-10">
            <h1 class="text-4xl font-bold text-text-primary mb-2">
              Music Library
            </h1>
            <p class="text-text-secondary">
              What would you like to play?
            </p>
          </div>
          <div
            ref="cardGrid"
            class="grid grid-cols-3 gap-8 w-full max-w-5xl"
            :class="{'h-60': !isMobileDevice, 'h-40': isMobileDevice}"
          >
            <PieceCategoryCard
              v-for="card in categoryCards"
              :key="card.value"
              :title="card.title"
              :subtitle="card.subtitle"
              :icon="card.icon"
              @click="handleCategorySelect(card.value)"
            />
          </div>
        </div>

        <!-- Layer 2: Difficulty (song/exercise) or lesson selection -->
        <div
          v-else-if="!selectedGroupId"
          key="layer-difficulty"
          class="absolute inset-0 flex flex-col items-center justify-center py-5"
        >
          <div class="text-center mb-4">
            <h1 class="text-4xl font-bold text-text-primary mb-2">
              {{ groupHeading }}
            </h1>
            <p class="text-text-secondary">
              {{ groupPrompt }}
            </p>
          </div>
          <div
            v-if="visibleGroups.length"
            class="grid gap-4 w-full overflow-y-auto pt-2"
            :class="groupGridClass"
          >
            <button
              v-for="group in visibleGroups"
              :key="group._id"
              class="diff-card"
              :class="{ 'diff-card--plain': !group.color }"
              :style="group.color ? cardStyle(group.color) : undefined"
              :aria-label="groupAriaLabel(group)"
              @click="handleGroupSelect(group._id)"
            >
              <h3 class="diff-card__label">
                {{ group.name }}
              </h3>
              <!-- Decorative: the button's aria-label already speaks the progress. -->
              <div
                v-if="group.progress"
                class="diff-card__progress"
                :class="{ 'is-complete': group.progress.isComplete }"
                aria-hidden="true"
              >
                <div class="diff-card__meter">
                  <div
                    class="diff-card__meter-fill"
                    :style="{ width: `${meterPercent(group.progress)}%` }"
                  />
                </div>
                <span class="diff-card__count">
                  <CheckCircleIcon v-if="group.progress.isComplete" class="w-5 h-5" />
                  <template v-else>{{ group.progress.passed }}/{{ group.progress.total }}</template>
                </span>
              </div>
            </button>
          </div>
          <p
            v-else
            class="text-text-secondary"
          >
            {{ groupEmptyMessage }}
          </p>
        </div>

        <!-- Layer 3: Piece list -->
        <SongsPieceList
          v-else
          key="layer-pieces"
          :category="selectedCategory!"
          :group-id="selectedGroupId"
          :group-name="selectedGroupName"
          :group-color="selectedGroupColor"
          class="absolute inset-0"
        />
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onActivated, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { Doc, Id } from '../../convex/_generated/dataModel'
import { useConvexQuery } from 'convex-vue'
import { api } from '../../convex/_generated/api'
import { useDeviceDetection } from '@/composables/useDeviceDetection'
import { goBackByRoute } from '@/composables/useRouteBack'
import IconButton from '@/components/IconButton.vue'
import SongsPieceList from '@/components/SongsPieceList.vue'
import { difficultyTextColors } from '@/utils/difficultyColor'
import { PIECE_CATEGORY_LABEL_PLURAL } from '@/utils/pieceCategory'
import PieceCategoryCard from '@/components/PieceCategoryCard.vue'
import { ChevronLeftIcon, MusicalNoteIcon, AcademicCapIcon, BookOpenIcon } from '@heroicons/vue/24/outline'
import { CheckCircleIcon } from '@heroicons/vue/24/solid'
import gsap from 'gsap'

// TODO: see if we can remove this define options
defineOptions({ name: 'PiecesView' })

const router = useRouter()
const route = useRoute()
const { isMobileDevice } = useDeviceDetection()

const { data: currentUser } = useConvexQuery(api.users.getCurrentUser, {})
const { data: allDifficulties } = useConvexQuery(api.difficulties.list, {})
const { data: allLessons } = useConvexQuery(api.lessons.listWithProgress, {})

type Category = Doc<'musicalPieces'>['category']

const selectedCategory = ref<Category>(undefined)
// Difficulty id for song/exercise, lesson id for lesson pieces — the second
// layer picks one of two parallel groupings, and only the queries below care which.
const selectedGroupId = ref<Id<'difficulties'> | Id<'lessons'>>()
const navDirection = ref<'forward' | 'backward'>('forward')
const layerTransitionName = computed(() => `layer-${navDirection.value}`)

// Track the route we came from so onActivated can decide whether to reset layer state.
let cameFromPieceGame = false
const stopAfterEach = router.afterEach((to, from) => {
  if (to.name === 'pieces') {
    cameFromPieceGame = from.name === 'piece-game'
  }
})
onBeforeUnmount(() => stopAfterEach())

onActivated(() => {
  if (cameFromPieceGame) return
  selectedCategory.value = undefined
  selectedGroupId.value = undefined
  navDirection.value = 'forward'
})

const categoryCards = [
  { value: 'song', title: PIECE_CATEGORY_LABEL_PLURAL.song, subtitle: 'Music to play along', icon: MusicalNoteIcon },
  { value: 'exercise', title: PIECE_CATEGORY_LABEL_PLURAL.exercise, subtitle: 'Practice & improve', icon: AcademicCapIcon },
  { value: 'lesson', title: PIECE_CATEGORY_LABEL_PLURAL.lesson, subtitle: 'Follow your lessons', icon: BookOpenIcon },
] as const

// Grid wrapper, so the entrance animation can stagger over its cards.
const cardGrid = ref<HTMLElement>()

const isLessonCategory = computed(() => selectedCategory.value === 'lesson')

type LessonProgress = { passed: number; total: number; isComplete: boolean }

// Layer 2 lists difficulties or lessons depending on the category; both are
// ordered name lists the student may be allow-listed against, so one shape
// covers them. Lessons carry no color, which the card falls back on, but do
// carry the viewer's progress through their pieces.
type GroupCard = {
  _id: Id<'difficulties'> | Id<'lessons'>
  name: string
  color?: string
  progress?: LessonProgress
}

const visibleGroups = computed<GroupCard[]>(() => {
  const user = currentUser.value
  const isStudent = user?.role === 'student'
  if (isLessonCategory.value) {
    const list = allLessons.value ?? []
    const allowed = new Set(user?.allowedLessons ?? [])
    const visible = isStudent ? list.filter(l => allowed.has(l._id)) : list
    return visible.map(l => ({
      _id: l._id,
      name: l.name,
      progress: { passed: l.passedPieces, total: l.totalPieces, isComplete: l.isComplete },
    }))
  }
  const list = allDifficulties.value ?? []
  const allowed = new Set(user?.allowedDifficulties ?? [])
  return isStudent ? list.filter(d => allowed.has(d._id)) : list
})

const groupHeading = computed(() =>
  selectedCategory.value ? PIECE_CATEGORY_LABEL_PLURAL[selectedCategory.value] : ''
)
const groupPrompt = computed(() =>
  isLessonCategory.value ? 'Choose your lesson' : 'Choose your difficulty'
)
const groupEmptyMessage = computed(() =>
  isLessonCategory.value ? 'No lessons available yet.' : 'No difficulties available yet.'
)

const groupGridClass = computed(() => {
  const n = visibleGroups.value.length
  const mobile = isMobileDevice.value
  if (n === 1) return ['grid-cols-1', 'max-w-xs',  mobile ? 'h-32' : 'h-48']
  if (n === 2) return ['grid-cols-2', 'max-w-lg',  mobile ? 'h-32' : 'h-48']
  if (n === 3) return ['grid-cols-3', 'max-w-2xl', mobile ? 'h-32' : 'h-48']
  return               ['grid-cols-2', 'max-w-2xl', mobile ? 'min-h-56' : 'min-h-80']
})

const selectedGroup = computed(() =>
  visibleGroups.value.find(g => g._id === selectedGroupId.value)
)
const selectedGroupName = computed(() => selectedGroup.value?.name ?? '')
const selectedGroupColor = computed(() => selectedGroup.value?.color ?? 'inherit')

// --dc tints the card; --dc-txt-light/dark keep the label legible in either theme.
function cardStyle(color: string) {
  const text = difficultyTextColors(color)
  return { '--dc': color, '--dc-txt-light': text.light, '--dc-txt-dark': text.dark }
}

function meterPercent(progress: LessonProgress) {
  return progress.total > 0 ? (progress.passed / progress.total) * 100 : 0
}

// The meter is aria-hidden, so the whole readout has to live in the button's label.
function groupAriaLabel(group: GroupCard) {
  if (!group.progress) return group.name
  const { passed, total, isComplete } = group.progress
  if (isComplete) return `${group.name}, completed`
  return `${group.name}, ${passed} of ${total} pieces completed`
}

function handleCategorySelect(cat: NonNullable<Category>) {
  navDirection.value = 'forward'
  selectedCategory.value = cat
}

function handleGroupSelect(id: Id<'difficulties'> | Id<'lessons'>) {
  navDirection.value = 'forward'
  selectedGroupId.value = id
}

function handleBack() {
  if (selectedGroupId.value) {
    navDirection.value = 'backward'
    selectedGroupId.value = undefined
  } else if (selectedCategory.value) {
    navDirection.value = 'backward'
    selectedCategory.value = undefined
  } else {
    goBackByRoute(router, route)
  }
}

// Entrance animation for category cards
onMounted(() => {
  const cards = Array.from(cardGrid.value?.children ?? []) as HTMLElement[]
  if (!cards.length) return
  gsap.set(cards, { opacity: 0, y: 50 })
  gsap.to(cards, {
    opacity: 1,
    y: 0,
    duration: 0.8,
    stagger: 0.2,
    ease: 'back.out(1.7)',
    delay: 0.2,
    onComplete: () => {
      cards.forEach((el) => { el.style.transitionDuration = '0.3s' })
    }
  })
})
</script>

<style scoped>
.layer-forward-enter-active,
.layer-forward-leave-active,
.layer-backward-enter-active,
.layer-backward-leave-active {
  position: absolute;
  inset: 0;
  transition: transform 0.35s ease, opacity 0.35s ease;
}

.layer-forward-enter-from {
  transform: translateX(100%);
  opacity: 0;
}
.layer-forward-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.layer-backward-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}
.layer-backward-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* ── Difficulty / lesson cards ────────────────────────────── */
.diff-card {
  --dc: var(--color-neutral-muted);
  /* The card's foreground: the accent where one is legible, plain text
     otherwise. Label and meter both draw from it so they can't drift apart. */
  --dc-ink: var(--dc-txt-light, var(--dc));
  /* Completion green, matching GradeBadge's A tier — per-theme for AA contrast. */
  --dc-done: #15803d;
  background: color-mix(in srgb, var(--dc) 14%, var(--color-surface));
  border-radius: 14px;
  border: 1px solid color-mix(in srgb, var(--dc) 45%, transparent);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 16px;
  cursor: pointer;
  text-align: left;
  height: 100%;
  transition: transform 0.2s ease,
              background 0.2s ease,
              box-shadow 0.2s ease;
}
:global(.dark) .diff-card {
  --dc-ink: var(--dc-txt-dark, var(--dc));
  --dc-done: #86efac;
}

/* Lessons carry no color of their own, so they take the plain surface
   treatment. Listed after the dark rule above so it wins in both themes. */
.diff-card--plain,
:global(.dark) .diff-card--plain {
  --dc: var(--color-primary);
  --dc-ink: var(--color-text-primary);
}

.diff-card:hover {
  transform: translateY(-4px);
  background: color-mix(in srgb, var(--dc) 24%, var(--color-surface));
  box-shadow: 0 8px 24px color-mix(in srgb, var(--dc) 25%, transparent);
}
.diff-card:focus-visible {
  outline: 2px solid var(--dc);
  outline-offset: 2px;
}

.diff-card__label {
  font-size: 1.6rem;
  font-weight: 900;
  color: var(--dc-ink);
  line-height: 1;
  letter-spacing: 0.03em;
}

/* ── Lesson progress readout ──────────────────────────────── */
.diff-card__progress {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
}

.diff-card__meter {
  flex: 1;
  height: 6px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--dc-ink) 18%, transparent);
  overflow: hidden;
}

.diff-card__meter-fill {
  height: 100%;
  border-radius: inherit;
  background: var(--dc-ink);
  transition: width 0.4s ease;
}

/* Fixed cell, so "3/5", "10/12" and the check mark all leave the meter the
   same width and the row the same height — neither may move as a student
   passes pieces. Height matches the check mark's own 20px box. */
.diff-card__count {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 2.75rem;
  height: 1.25rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--color-text-secondary);
}

.diff-card__progress.is-complete .diff-card__meter-fill { background: var(--dc-done); }
.diff-card__progress.is-complete .diff-card__count { color: var(--dc-done); }
</style>
